import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LINKS } from '../constants/links';
import { signin } from '../services/api';
import { toast } from 'react-toastify';
import { Eye, EyeOff } from 'lucide-react';
import { emailPattern } from '../utils/authSecurity';
import { useUser } from '../context/UserContext';

export default function Signin() {
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const hasShownSignupToast = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !emailPattern.test(email)) {
      setError('Not a valid email address.');
      return;
    }

    if (!password) {
      setError('Please fill in all fields.');
      return;
    }

    try {
      const token = await signin(email, password);
      sessionStorage.setItem('token', token);

      await refreshUser(token);

      navigate('/');
      sessionStorage.setItem('fromSignin', 'true');
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes('Invalid credentials')) {
          setError('Invalid email or password');
        } else {
          setError(err.message);
        }
      } else {
        setError('Network error');
      }
    }
  };

  useEffect(() => {
    const fromSignup = sessionStorage.getItem('fromSignup');
    if (fromSignup && !hasShownSignupToast.current) {
      toast.success('Account created successfully! Please sign in.');
      sessionStorage.removeItem('fromSignup');
      hasShownSignupToast.current = true;
    }
  }, []);

  return (
    <div className="flex flex-col items-center bg-gray-50 dark:bg-neutral-900 px-4 py-24 gap-4">
      <div className="bg-white dark:bg-neutral-800 p-8 rounded-lg shadow-lg w-full max-w-sm">
        <h2 className="text-2xl font-semibold text-center text-gray-800 dark:text-gray-200 mb-6">
          Sign In
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              className="mt-1 block w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@domain.com"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="mt-1 block w-full px-4 py-2 pr-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
              />
              <button
                type="button"
                className="absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-500 dark:text-gray-300"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition"
          >
            Sign In
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Don’t have an account?{' '}
            <Link
              to={LINKS.SIGNUP}
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              Sign up
            </Link>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Forgot your password?{' '}
            <Link
              to={LINKS.RESET_PASSWORD}
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              Reset Password
            </Link>
          </p>
        </div>
      </div>

      <div className="w-full max-w-sm rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-6 py-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
          Why create an account?
        </p>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-blue-500">✦</span>
            Get notified about dataset updates and new releases
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-blue-500">✦</span>
            Access your full download history and re-download scripts at any
            time
          </li>
        </ul>
      </div>
    </div>
  );
}
